import { useState, useEffect, useCallback } from 'react'
import { projectsApi, type ApiProject, type ApiProjectList } from '../utils/api'

const STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Черновик', PUBLISHED: 'Опубликован', ARCHIVED: 'В архиве',
  BUILDING: 'Сборка', PREVIEW: 'Превью', DEPLOYING: 'Деплой',
  LIVE: 'Запущен', FAILED: 'Ошибка', SUSPENDED: 'Приостановлен',
}
const STATUS_COLOR: Record<string, string> = {
  DRAFT: '#9ca3af', PUBLISHED: '#10b981', ARCHIVED: '#6b7280',
  BUILDING: '#f59e0b', PREVIEW: '#3b82f6', DEPLOYING: '#8b5cf6',
  LIVE: '#10b981', FAILED: '#ef4444', SUSPENDED: '#f97316',
}
const TYPE_LABEL: Record<string, string> = {
  WEBSITE: '🌐 Сайт', HOSTING: '🚀 Хостинг', BACKEND: '🗄️ Backend',
  FULLSTACK: '⚡ Fullstack', MOBILE: '📱 Мобильное',
}

interface CreateForm {
  open: boolean
  kind: 'website' | 'code'
  name: string
  desc: string
  error: string | null
  loading: boolean
}

export function ProjectsPanel() {
  const [list, setList] = useState<ApiProjectList | null>(null)
  const [loading, setLoading] = useState(true)
  const [cursor, setCursor] = useState<string | null>(null)
  const [form, setForm] = useState<CreateForm>({ open: false, kind: 'website', name: '', desc: '', error: null, loading: false })

  const load = useCallback(async (next?: string) => {
    setLoading(true)
    try {
      const res = await projectsApi.list(next ?? undefined, 20)
      setList(prev => next && prev
        ? { ...res, items: [...prev.items, ...res.items] }
        : res
      )
      setCursor(res.nextCursor)
    } catch (e) {
      console.error('ProjectsPanel load error', e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const handleCreate = async () => {
    if (!form.name.trim()) {
      setForm(f => ({ ...f, error: 'Введите название проекта' }))
      return
    }
    setForm(f => ({ ...f, loading: true, error: null }))
    try {
      let project: ApiProject
      if (form.kind === 'website') {
        project = await projectsApi.createWebsite({ name: form.name.trim(), description: form.desc.trim() || undefined })
      } else {
        project = await projectsApi.createCodeProject({ name: form.name.trim(), description: form.desc.trim() || undefined })
      }
      setList(prev => prev ? { ...prev, items: [project, ...prev.items] } : { items: [project], hasMore: false, nextCursor: null })
      setForm({ open: false, kind: 'website', name: '', desc: '', error: null, loading: false })
    } catch (e) {
      setForm(f => ({ ...f, loading: false, error: e instanceof Error ? e.message : 'Ошибка создания проекта' }))
    }
  }

  const handleArchive = async (p: ApiProject) => {
    if (!confirm(`Архивировать проект «${p.name}»?`)) return
    try {
      await projectsApi.archive(p.id)
      setList(prev => prev ? { ...prev, items: prev.items.map(i => i.id === p.id ? { ...i, status: 'ARCHIVED' } : i) } : prev)
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Ошибка')
    }
  }

  return (
    <div style={{ padding: '0' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: 800, margin: '0 0 4px', color: '#111' }}>Проекты</h1>
          <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
            {list ? `${list.items.length} проект${list.items.length === 1 ? '' : list.items.length < 5 ? 'а' : 'ов'}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => setForm(f => ({ ...f, open: true, kind: 'website' }))}
            style={{ padding: '10px 18px', background: '#111', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
          >
            + Сайт
          </button>
          <button
            onClick={() => setForm(f => ({ ...f, open: true, kind: 'code' }))}
            style={{ padding: '10px 18px', background: '#5b6af0', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
          >
            + Code Hosting
          </button>
        </div>
      </div>

      {/* Create modal */}
      {form.open && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '16px', width: '440px', maxWidth: '92vw', padding: '32px', boxShadow: '0 24px 64px rgba(0,0,0,0.2)' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 800, margin: '0 0 20px', color: '#111' }}>
              {form.kind === 'website' ? 'Новый сайт' : 'Новый Code Hosting проект'}
            </h2>
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#555', marginBottom: '6px' }}>Название *</label>
              <input
                autoFocus
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value, error: null }))}
                onKeyDown={e => { if (e.key === 'Enter') void handleCreate() }}
                placeholder={form.kind === 'website' ? 'Мой сайт' : 'My Next App'}
                style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#555', marginBottom: '6px' }}>Описание</label>
              <input
                value={form.desc}
                onChange={e => setForm(f => ({ ...f, desc: e.target.value }))}
                placeholder='Необязательно'
                style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>
            {form.error && (
              <div style={{ marginBottom: '14px', padding: '10px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', fontSize: '13px', color: '#dc2626' }}>
                {form.error}
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setForm(f => ({ ...f, open: false, name: '', desc: '', error: null }))}
                style={{ padding: '9px 18px', background: '#f3f4f6', color: '#555', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
              >
                Отмена
              </button>
              <button
                onClick={() => void handleCreate()}
                disabled={form.loading}
                style={{ padding: '9px 18px', background: '#111', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: form.loading ? 'wait' : 'pointer', opacity: form.loading ? 0.7 : 1 }}
              >
                {form.loading ? '...' : 'Создать'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Project list */}
      {loading && !list ? (
        <div style={{ textAlign: 'center', padding: '80px', color: '#888' }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>⟳</div>
          Загрузка...
        </div>
      ) : list && list.items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '80px 40px' }}>
          <div style={{ fontSize: '56px', marginBottom: '16px' }}>📁</div>
          <h2 style={{ fontSize: '20px', fontWeight: 800, margin: '0 0 8px', color: '#111' }}>Нет проектов</h2>
          <p style={{ fontSize: '14px', color: '#666', margin: '0 0 28px' }}>Создайте первый проект — сайт или code hosting</p>
          <button
            onClick={() => setForm(f => ({ ...f, open: true, kind: 'website' }))}
            style={{ padding: '11px 24px', background: '#111', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}
          >
            + Создать проект
          </button>
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
            {list?.items.map(p => (
              <ProjectCard key={p.id} project={p} onArchive={() => void handleArchive(p)} />
            ))}
          </div>
          {list?.hasMore && (
            <div style={{ textAlign: 'center', marginTop: '32px' }}>
              <button
                onClick={() => void load(cursor ?? undefined)}
                disabled={loading}
                style={{ padding: '10px 24px', background: '#f3f4f6', color: '#555', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: loading ? 'wait' : 'pointer' }}
              >
                {loading ? 'Загрузка...' : 'Загрузить ещё'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function ProjectCard({ project, onArchive }: { project: ApiProject; onArchive: () => void }) {
  const statusColor = STATUS_COLOR[project.status] ?? '#9ca3af'
  const statusLabel = STATUS_LABEL[project.status] ?? project.status
  const typeLabel = TYPE_LABEL[project.type] ?? project.type
  const updatedAgo = formatAgo(project.updatedAt)

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '14px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '15px', fontWeight: 700, color: '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {project.name}
          </div>
          <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>/{project.slug}</div>
        </div>
        <span style={{ flexShrink: 0, fontSize: '11px', fontWeight: 700, color: statusColor, background: `${statusColor}18`, padding: '2px 8px', borderRadius: '99px', border: `1px solid ${statusColor}40` }}>
          {statusLabel}
        </span>
      </div>

      <div style={{ fontSize: '12px', color: '#666' }}>{typeLabel}</div>

      {project.description && (
        <div style={{ fontSize: '12px', color: '#888', lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
          {project.description}
        </div>
      )}

      <div style={{ fontSize: '11px', color: '#aaa', marginTop: 'auto' }}>
        Обновлён {updatedAgo}
      </div>

      {project.status !== 'ARCHIVED' && (
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={onArchive}
            style={{ flex: 1, padding: '6px', background: '#f9fafb', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: '7px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
          >
            Архив
          </button>
        </div>
      )}
    </div>
  )
}

function formatAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const min = Math.floor(diff / 60000)
  if (min < 1) return 'только что'
  if (min < 60) return `${min} мин. назад`
  const h = Math.floor(min / 60)
  if (h < 24) return `${h} ч. назад`
  const d = Math.floor(h / 24)
  if (d < 30) return `${d} д. назад`
  return new Date(iso).toLocaleDateString('ru')
}
